import { useState, useEffect } from "react";

export interface SubTab {
	name: string;
	current: boolean;
	content: React.ReactNode;
}

export interface TabsProps {
	initialSubTabs: SubTab[];
}

export default function Tabs({ initialSubTabs }: TabsProps) {
	const [tabs, setTabs] = useState(initialSubTabs);

	// Sync tabs when initialSubTabs changes, but preserve the currently active tab
	useEffect(() => {
		setTabs((prevTabs) => {
			const activeTabName = prevTabs.find((t) => t.current)?.name;
			return initialSubTabs.map((tab) => ({
				...tab,
				current: activeTabName ? tab.name === activeTabName : tab.current,
			}));
		});
	}, [initialSubTabs]);

	function handleSubTabChange(name: string): void {
		setTabs((prev) =>
			prev.map((tab) =>
				tab.name === name ? { ...tab, current: true } : { ...tab, current: false }
			)
		);
	}

	const activeTab = tabs.find((t) => t.current);
	return (
		<div className="mt-8 px-4">
			<div
				aria-label="Activity Tabs"
				className="flex divide-x divide-gray-200 rounded-lg shadow-sm items-center hover:bg-gray-100">
				{tabs.map((tab, tabIdx) => (
					<button
						onClick={() => handleSubTabChange(tab.name)}
						key={tab.name}
						aria-current={tab.current ? "page" : undefined}
						className={`${
							tab.current
								? "text-gray-900 !text-lg bg-gray-100 border-b-2 border-b-[var(--subtab-border)]"
								: "text-gray-500 hover:text-gray-700 border-b !border-b-transparent hover:bg-gray-100"
						} 
									${tabIdx === 0 ? "rounded-l-lg" : tabIdx === tabs.length - 1 ? "rounded-r-lg" : ""} 
									group flex-1 relative p-4 text-center text-sm h-full font-medium hover:cursor-pointer focus:z-10`}>
						<span>{tab.name}</span>
					</button>
				))}
			</div>
			<div>{activeTab?.content}</div>
		</div>
	);
}
